/**
 * AVBP — admin money-gate OVERRIDE + offline mark-paid client layer.
 * Phase 1 Task C · scenario SC-058.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  WHAT THIS IS
 * ─────────────────────────────────────────────────────────────────────────
 *  Owner ruling (Gate-1, SC-013): an admin may override a money/deliverable
 *  gate; every override MUST (a) write an immutable audit ledger entry and
 *  (b) trigger a staged payment-reminder mechanism.
 *
 *  This is the thin portal seam over the two Cloud Functions:
 *    recordGateOverride  — admin-only. Clears the gate server-side, writes
 *                          the money.gate.override.audit ledger entry,
 *                          seeds the staged reminder (INERT).
 *    markPaidManual      — admin/staff. Records an offline payment, clears
 *                          the gate, stops the reminder loop.
 *
 *  The audit entry + reminder are created ENTIRELY server-side (the gate is
 *  server-authoritative — Task B firestore.rules). This module only
 *  invokes; it never writes gateState itself.
 *
 *  PURE/IMPURE split: `gateOverrideCapability`, `validateOverrideInput` are
 *  pure + unit-tested. `recordGateOverride` / `markPaidManual` are the
 *  thin callable seam.
 * ─────────────────────────────────────────────────────────────────────────
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import type { Role } from './roles';

/* ════════════════════ Capability resolution (PURE) ════════════════════ */

/**
 * Who may exercise `money.gate.override`? Admin ONLY (spec capability
 * catalog: defaultRoles=['admin'], grantable=false). This is the UX
 * predicate — the CF re-checks isAdminCaller server-side regardless.
 */
export function canOverrideGate(role: Role | null | undefined): boolean {
  return role === 'admin';
}

/**
 * Who may record an offline payment (`payments.markPaid.manual`)? Admin OR
 * staff (spec catalog defaultRoles=['admin','staff']). The CF re-checks
 * isTeamCaller server-side.
 */
export function canMarkPaidManual(role: Role | null | undefined): boolean {
  return role === 'admin' || role === 'staff';
}

/* ════════════════════ Input validation (PURE) ════════════════════ */

export type GateKind = 'money' | 'curation';

export interface OverrideInput {
  projectId: string;
  gateKind: GateKind;
  /** Required for a money gate; ignored for curation. */
  gateId?: string;
  /** Negotiation context — permanently audited. Mandatory, ≥4 chars. */
  reason: string;
  /** Amount already received (drives the outstanding-balance calc). */
  amountPaid?: number;
}

/**
 * PURE — validate override input before the round-trip. Returns the first
 * problem, or null when valid. Mirrors the CF's own argument checks so the
 * UI fails fast with a clear message.
 */
export function validateOverrideInput(
  input: Partial<OverrideInput>,
): string | null {
  if (!input.projectId) return 'No project selected.';
  if (input.gateKind !== 'money' && input.gateKind !== 'curation') {
    return 'Pick which gate to override.';
  }
  if (input.gateKind === 'money' && !input.gateId) {
    return 'Pick which money gate to override.';
  }
  const reason = (input.reason ?? '').trim();
  if (reason.length < 4) {
    return 'A reason is required — it is permanently recorded in the audit log.';
  }
  if (reason.length > 1000) return 'Reason is too long.';
  return null;
}

/* ════════════════════ Callable seam (impure) ════════════════════ */

export interface OverrideResult {
  ok: boolean;
  ledgerId: string;
  reminderId: string;
  outstandingINR: number;
  reminderStatus: string;
  /** Always false — the reminder is created INERT (armed_pending). */
  reminderArmed: boolean;
}

/**
 * Exercise an admin money-gate override. The Cloud Function (admin SDK)
 * does all three writes atomically — gate clearance + immutable audit
 * entry + seeded (inert) reminder. Throws on validation/permission error.
 */
export async function recordGateOverride(
  input: OverrideInput,
): Promise<OverrideResult> {
  const problem = validateOverrideInput(input);
  if (problem) throw new Error(problem);
  const fn = httpsCallable<OverrideInput, OverrideResult>(
    functions,
    'recordGateOverride',
  );
  const res = await fn({
    ...input,
    gateId: input.gateKind === 'curation' ? 'curation' : input.gateId,
    reason: input.reason.trim(),
  });
  return res.data;
}

export type PayMethod = 'cash' | 'bank_transfer' | 'cheque' | 'upi' | 'other';

export interface MarkPaidInput {
  projectId: string;
  gateKind: GateKind;
  gateId?: string;
  amountINR: number;
  method?: PayMethod;
  reference?: string;
  /** Optional — the reminder doc to stop. Omit → all running reminders
   *  on the project are stopped. */
  reminderId?: string;
}

export interface MarkPaidResult {
  ok: boolean;
  paymentId: string;
  ledgerId: string;
  stoppedReminderId: string | null;
}

/**
 * Record an OFFLINE payment (cash / bank transfer received outside
 * Razorpay). The CF writes a payment_event, clears the gate, and STOPS the
 * staged reminder loop. This is the offline arm of "the loop stops when
 * the balance is paid" (SC-058).
 */
export async function markPaidManual(
  input: MarkPaidInput,
): Promise<MarkPaidResult> {
  if (!input.projectId) throw new Error('No project selected.');
  if (!(input.amountINR > 0)) {
    throw new Error('Enter the amount received.');
  }
  const fn = httpsCallable<MarkPaidInput, MarkPaidResult>(
    functions,
    'markPaidManual',
  );
  const res = await fn({
    ...input,
    gateId: input.gateKind === 'curation' ? 'curation' : input.gateId,
  });
  return res.data;
}
